/**
 * transports/ws-transport.ts
 *
 * SessionTransport over a persistent WebSocket connection.
 *
 * Multiplexing:
 *   All sessions share one WebSocket. Every message carries sessionId or
 *   correlationId so both sides route correctly without per-session connections.
 *
 * Bidirectional flow:
 *   Downstream  ACP → Backend:  JSON-serialised DownstreamMessage via ws.send()
 *   Upstream    Backend → ACP:  JSON-parsed UpstreamMessage via onmessage handler
 *
 * Correlated round-trips (fs, terminal, permission):
 *   Backend echoes correlationId in its upstream response.
 *   For example, PineSeedAgent matches and resolves via pendingCorrelated.
 *
 * Auth:
 *   Configured via HttpAuth — bearer, api_key, or basic.
 *   Auth headers are sent on the WebSocket upgrade request.
 *   Use wss:// — credentials are plaintext on the wire otherwise.
 */

import type * as acp from "@agentclientprotocol/sdk";
import { resolveAuthHeaders } from "./env";
import type { HttpAuth } from "./env";
import type {
  SessionTransport,
  SessionInitState,
  DownstreamMessage,
  UpstreamMessage,
} from "./session-bus";

// ─── Config ──────────────────────────────────────────────────────────────────

export interface WsTransportConfig {
  /** wss:// URL of the backend WebSocket endpoint */
  url: string;
  /** Auth sent on the WebSocket upgrade request. Required by connect(); unused by injectWebSocket(). */
  auth?: HttpAuth;
  /** Timeout in milliseconds for loadSession history replay */
  ws?: WebSocket;
  loadSessionTimeoutMs: number;
  /** Capabilities returned verbatim in ACP initialize() — backend owns this */
  capabilities: acp.InitializeResponse;
  /** Additional headers merged into the WebSocket upgrade request after auth headers */
  extraHeaders?: Record<string, string>;
  /**
   * Called when the WebSocket closes unexpectedly during an active session
   * (i.e. after all pending opens are settled). Use this to reject any
   * in-flight prompt turns so they surface as JSON-RPC errors rather than
   * hanging forever or crashing the process.
   */
  onClose?: () => void;
}

// ─── Transport ───────────────────────────────────────────────────────────────

export class WsTransport implements SessionTransport {
  private config: WsTransportConfig;

  // Single upstream handler registered at construction.
  // Initialised to a no-op so one-shot intercepts can always restore a valid function.
  private upstreamHandler: (msg: UpstreamMessage) => Promise<void> =
    async () => {};

  // Pending openSession replies: key = sessionId, resolved on "session_opened" upstream msg
  private pendingOpen = new Map<
    string,
    {
      resolve: (value: SessionInitState) => void;
      reject: (err: unknown) => void;
    }
  >();

  constructor(config: WsTransportConfig) {
    this.config = config;
  }

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  /**
   * Attach a pre-opened WebSocket (browser path — cookie auth, no headers).
   * The socket must already be in OPEN state. Wires onmessage/onclose
   * identically to connect() without constructing a new WebSocket.
   * Never call connect() after this.
   */
  injectWebSocket(ws: WebSocket): void {
    if (ws.readyState !== WebSocket.OPEN) {
      throw new Error(
        `[WsTransport] injectWebSocket: socket must be OPEN (readyState=${ws.readyState})`,
      );
    }
    this.config.ws = ws;
    this.config.ws.onmessage = (event) => {
      let msg: UpstreamMessage;
      try {
        msg = JSON.parse(
          typeof event.data === "string" ? event.data : event.data.toString(),
        ) as UpstreamMessage;
      } catch (err) {
        console.error("[WsTransport] failed to parse upstream message:", err);
        console.error(
          "[WsTransport] raw frame:",
          typeof event.data === "string"
            ? event.data.slice(0, 200)
            : "(binary)",
        );
        return;
      }

      // console.error(
      //   `[WsTransport] ← raw: ${typeof event.data === "string" ? event.data.slice(0, 300) : "(binary)"}`,
      // );
      // console.error(`[WsTransport] ← ${(msg as any).type ?? "unknown"}`);

      if ((msg as any).type === "session_opened") {
        const pending = this.pendingOpen.get((msg as any).sessionId);
        if (pending) {
          this.pendingOpen.delete((msg as any).sessionId);
          pending.resolve((msg as any).result);
        }
        return;
      }

      if ((msg as any).type === "session_open_failed") {
        const pending = this.pendingOpen.get((msg as any).sessionId);
        if (pending) {
          this.pendingOpen.delete((msg as any).sessionId);
          const { code, message } = (msg as any).error as {
            code: number;
            message: string;
          };
          pending.reject(Object.assign(new Error(message), { code }));
        }
        return;
      }

      this.upstreamHandler(msg).catch((err) =>
        console.error("[WsTransport] upstream handler error:", err),
      );
    };
    this.config.ws.onclose = () => {
      for (const [, pending] of this.pendingOpen) {
        pending.reject(
          new Error(
            "[WsTransport] WebSocket closed unexpectedly during session open",
          ),
        );
      }
      this.pendingOpen.clear();
      this.config.onClose?.();
    };
  }

  /** Call once at agent startup. Resolves when the WebSocket is open. */
  connect(): Promise<void> {
    if (!this.config.ws && !this.config.auth) {
      return Promise.reject(
        new Error("[WsTransport] connect: auth is required when no WebSocket is injected"),
      );
    }
    const auth = this.config.auth;
    return new Promise((resolve, reject) => {
      this.config.ws = new WebSocket(this.config.url, {
        headers: {
          // Auth not passed in for injected WS.
          // Browser env relies on session cookie auth
          ...(this.config.ws || !auth ? {} : resolveAuthHeaders(auth)),
          ...this.config.extraHeaders,
        },
      } as unknown as string[]);

      this.config.ws.onopen = () => resolve();
      this.config.ws.onerror = (e) => {
        const msg =
          e instanceof ErrorEvent && e.message
            ? e.message
            : "WebSocket connection failed";
        reject(new Error(`[WsTransport] ${msg}`));
      };

      this.config.ws.onmessage = (event) => {
        const raw =
          typeof event.data === "string" ? event.data : event.data.toString();
        let msg: UpstreamMessage;
        try {
          msg = JSON.parse(raw) as UpstreamMessage;
        } catch (err) {
          console.error("[WsTransport] failed to parse upstream message:", err);
          console.error("[WsTransport] raw frame:", raw.slice(0, 200));
          return;
        }
        // Route "session_opened" to pending openSession() waiters
        if ((msg as any).type === "session_opened") {
          const pending = this.pendingOpen.get((msg as any).sessionId);
          if (pending) {
            this.pendingOpen.delete((msg as any).sessionId);
            pending.resolve((msg as any).result);
          }
          return;
        }

        if ((msg as any).type === "session_open_failed") {
          const pending = this.pendingOpen.get((msg as any).sessionId);
          if (pending) {
            this.pendingOpen.delete((msg as any).sessionId);
            const { code, message } = (msg as any).error as {
              code: number;
              message: string;
            };
            pending.reject(Object.assign(new Error(message), { code }));
          }
          return;
        }

        // All other messages go to the single upstream handler
        this.upstreamHandler(msg).catch((err) =>
          console.error("[WsTransport] upstream handler error:", err),
        );
      };

      this.config.ws.onclose = () => {
        // Reject any sessions waiting on open_session — the error propagates up.
        // The stdio stream stays open; Implementation re-runs the factory on the
        // next initialize() so the client can retry without restarting.
        for (const [, pending] of this.pendingOpen) {
          pending.reject(
            new Error(
              'WebSocket closed — server may be unreachable or credentials expired. Run "pineseed login" if the problem persists.',
            ),
          );
        }
        this.pendingOpen.clear();
        // Notify consumer so it can reject any in-flight prompt turns.
        // Without this, prompt() hangs forever after a mid-session drop.
        this.config.onClose?.();
      };
    });
  }

  disconnect(): Promise<void> {
    this.config.ws?.close();
    return Promise.resolve();
  }

  // ─── SessionTransport implementation ──────────────────────────────────────

  getCapabilities(): Promise<acp.InitializeResponse> {
    return Promise.resolve(this.config.capabilities);
  }

  /**
   * Sends "open_session" downstream and awaits "session_opened" upstream
   * carrying initial state (modes, configOptions).
   */
  async openSession(
    sessionId: string,
    params: acp.NewSessionRequest,
    clientCapabilities: acp.ClientCapabilities,
  ): Promise<SessionInitState> {
    // Reconnect if socket is closed or missing (e.g. after credential expiry)
    const needsReconnect =
      !this.config.ws ||
      this.config.ws.readyState === WebSocket.CLOSED ||
      this.config.ws.readyState === WebSocket.CLOSING;
    if (needsReconnect) {
      await this.connect();
    }
    return new Promise((resolve, reject) => {
      this.pendingOpen.set(sessionId, { resolve, reject });
      this.rawSendAsync({
        type: "open_session" as any,
        sessionId,
        params,
        clientCapabilities,
      }).catch(reject);
    });
  }

  async closeSession(sessionId: string): Promise<void> {
    this.pendingOpen.delete(sessionId);
    this.rawSend({ type: "cancel", sessionId });
  }

  async send(msg: DownstreamMessage): Promise<void> {
    // console.error(`[WsTransport] → ${msg.type}`);
    await this.rawSendAsync(msg);
  }

  onMessage(handler: (msg: UpstreamMessage) => Promise<void>): void {
    this.upstreamHandler = handler;
  }

  // ─── Optional: session loading ────────────────────────────────────────────
  // list_sessions flows through the bus: downstream list_sessions →
  // upstream list_sessions_response. No direct method needed here.

  loadSession(
    sessionId: string,
    params: acp.LoadSessionRequest,
    clientCapabilities: acp.ClientCapabilities,
  ): Promise<acp.LoadSessionResponse> {
    // Subscribe to the session channel so history replay update messages are
    // received. We do NOT call openSession here — that would send open_session
    // to the server and overwrite the existing cwd→sessionId connector mapping.
    // The pendingOpen entry lets session_opened route correctly if the server
    // sends it, but load_session drives the handshake via load_session_response.
    return new Promise((resolve, reject) => {
      const original = this.upstreamHandler;

      const intercept = async (msg: UpstreamMessage): Promise<void> => {
        if (
          (msg as any).type === "load_session_response" &&
          (msg as any).sessionId === sessionId
        ) {
          this.upstreamHandler = original;
          resolve((msg as any).result);
          return;
        }
        return original(msg);
      };

      this.upstreamHandler = intercept;

      this.rawSend({
        type: "load_session" as any,
        sessionId,
        params,
        clientCapabilities,
      });

      setTimeout(() => {
        this.upstreamHandler = original;
        reject(
          new Error(
            `[WsTransport] loadSession timed out after ${this.config.loadSessionTimeoutMs}ms`,
          ),
        );
      }, this.config.loadSessionTimeoutMs);
    });
  }

  // ─── Internal ─────────────────────────────────────────────────────────────

  private async rawSendAsync(msg: unknown): Promise<void> {
    if (!this.config.ws || this.config.ws.readyState !== WebSocket.OPEN) {
      // Socket is closed — reconnect once before retrying. Only fires on
      // user-initiated actions (send/newSession), never in the background.
      await this.connect();
    }
    this.config.ws!.send(JSON.stringify(msg));
  }

  private rawSend(msg: unknown): void {
    if (!this.config.ws || this.config.ws.readyState !== WebSocket.OPEN) {
      throw new Error("[WsTransport] WebSocket is not open");
    }
    this.config.ws.send(JSON.stringify(msg));
  }
}
